import type { LocationSource } from './location.js';

/**
 * How compliance times and places read for people (brand/voice.md: short, plain). Pure apart from Intl.
 */

const cache = new Map<string, Intl.DateTimeFormat>();
function fmt(key: string, make: () => Intl.DateTimeFormat): Intl.DateTimeFormat {
  let f = cache.get(key);
  if (!f) {
    f = make();
    cache.set(key, f);
  }
  return f;
}

/** "Tue 9:02 AM" in `zone` (the viewer's zone when undefined). */
export function formatDayTime(at: Date, zone?: string): string {
  const f = fmt(
    `dt:${zone ?? ''}`,
    () =>
      new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
        ...(zone ? { timeZone: zone } : {}),
      }),
  );
  return f.format(at);
}

/** "9:02 AM" in `zone`. */
export function formatClock(at: Date, zone?: string): string {
  const f = fmt(
    `t:${zone ?? ''}`,
    () =>
      new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        ...(zone ? { timeZone: zone } : {}),
      }),
  );
  return f.format(at);
}

/** "EDT", "CST", "HST" — the zone's short name at that moment (falls back to the zone id). */
export function zoneAbbreviation(zone: string, at: Date): string {
  const f = fmt(
    `tz:${zone}`,
    () => new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' }),
  );
  return f.formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? zone;
}

/** Minutes after midnight → "8:00 AM" (a rule's local time, no zone). */
export function formatLocalTimeOfDay(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = String(minutes % 60).padStart(2, '0');
  return `${h % 12 === 0 ? 12 : h % 12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
}

const ZONE_NAMES: Readonly<Record<string, string>> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Phoenix': 'Arizona (no daylight saving)',
  'America/Los_Angeles': 'Pacific',
  'America/Anchorage': 'Alaska',
  'America/Adak': 'Hawaii-Aleutian (Adak)',
  'Pacific/Honolulu': 'Hawaii',
};

/** "Eastern", "Central"… for the US zones we use; the zone id otherwise. */
export function zoneName(zone: string): string {
  return ZONE_NAMES[zone] ?? zone;
}

/**
 * A time in each candidate zone: "Tue 9:02 AM EDT" or "Tue 9:02 AM EDT · 8:02 AM CDT". Times in several
 * zones share the weekday of the first.
 */
export function formatInZones(at: Date, zones: readonly string[]): string {
  return zones
    .map(
      (z, i) => `${i === 0 ? formatDayTime(at, z) : formatClock(at, z)} ${zoneAbbreviation(z, at)}`,
    )
    .join(' · ');
}

export function describeLocationSource(source: LocationSource, mismatch: boolean): string {
  if (source === 'manual') return 'Set by an admin';
  if (source === 'state_and_area_code' || mismatch) {
    return "The state and the area code don't match, so both apply";
  }
  if (source === 'area_code') return 'From the area code';
  return 'From the state';
}
